import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./useAuth";
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../../config/firebase";

export const useDeviceToken = () => {
  const { user } = useAuth();
  const [deviceToken, setDeviceToken] = useState(null);

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.data && event.data.type === "FCM_TOKEN") {
        const newToken = event.data.token;
        setDeviceToken(newToken);

        // Update the driver's fcmTokens array in Firestore
        if (user && newToken) {
          try {
            const driverRef = doc(db, "drivers", user.uid);
            await updateDoc(driverRef, {
              fcmTokens: arrayUnion(newToken),
              lastTokenUpdate: new Date().toISOString(),
            });
          } catch (error) {
            console.error("Error updating FCM token:", error);
          }
        }
      }
    };

    // Listen for token from native app
    window.addEventListener("message", handleMessage);

    // Request token from native app
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "REQUEST_FCM_TOKEN",
        })
      );
    } else {
      console.warn("ReactNativeWebView not available - running in browser");
    }

    // Cleanup function to remove token when component unmounts
    return () => {
      window.removeEventListener("message", handleMessage);
      if (user && deviceToken) {
        const driverRef = doc(db, "drivers", user.uid);
        updateDoc(driverRef, {
          fcmTokens: arrayRemove(deviceToken),
        }).catch(error => {
          console.error("Error removing FCM token:", error);
        });
      }
    };
  }, [user]);

  return deviceToken;
};

export const useSendNotification = () => {
  const { user } = useAuth();

  const sendNotification = useCallback(
    async (driverId, message) => {
      if (!driverId) {
        throw new Error("Driver ID not provided");
      }

      try {
        const response = await fetch("https://europe-west2-zippedriveralpha.cloudfunctions.net/sendNotification", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await user.getIdToken()}`,
          },
          body: JSON.stringify({
            driverId,
            message,
            title: "New Notification",
            body: message,
            data: {},
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to send notification");
        }

        return await response.json();
      } catch (error) {
        console.error("Error sending notification:", error);
        throw error;
      }
    },
    [user]
  );

  return sendNotification;
};
